import { Component, OnInit, OnDestroy, ViewEncapsulation, HostListener } from '@angular/core';
import { Subscription } from 'rxjs';
import { AuthService } from '../../core/service/auth/auth.service';
import { OrganizationService } from '../../data/service/organization.service';
import { UserService } from '../../data/service/user.service';
import { ToasterMessageService } from '../../shared/toaster-message/toaster-message.service';
import { CurrentUserModel } from '../../data/model/register-model';
import { ToasterTypes } from '../../core/enum/toaster-type.enum';
import { SpinnerService } from '../../shared/spinner/spinner.service';
import { SharedService } from 'src/app/core/service/shared.service';
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { ScreenBrakepoints } from 'src/app/data/model/screen-brakepoints';
import { environment } from 'src/environments/environment';
import { GodModeService } from '../../data/service/god-mode.service';
import { PlanPromptService } from '../../shared/billing/plan-prompt.service';
import { MailboxConnectBridgeService } from '../../core/service/mailbox-connect-bridge.service';
import { Router } from '@angular/router';
import { PracticeInviteReturn } from '../../core/util/practice-invite-return';

@Component({
  selector: 'app-content-layout',
  templateUrl: './content-layout.component.html',
  styleUrls: ['./content-layout.component.scss'],
  encapsulation: ViewEncapsulation.None,
})

export class ContentLayoutComponent implements OnInit, OnDestroy {

  userDetails: any;
  subscriptions: Subscription[] = [];
  currentUserModel: CurrentUserModel;
  isOutlookRoute: boolean = environment.isOutlookEnv;
  isChromeRoute: boolean = environment.isChromeEnv;
  godModeUser: any = null;

  public IsQuickBookResponse: number;

  /**
   * SD-1248: Practice view (/clients and /clients/team). The menu is one organisation's, so it steps
   * aside and the page takes the full width; choosing an organisation leaves /clients and it returns.
   */
  get isPracticeView(): boolean {
    const path = (this.router.url || '').split('?')[0];
    return path === '/clients' || path.indexOf('/clients/') === 0;
  }

  @HostListener('window:resize', ['$event'])
  onResize(event) {
    this.setCustomJs();
  }

  constructor(
    private authService: AuthService,
    private organizationService: OrganizationService,
    private userService: UserService,
    public spinnerService: SpinnerService,
    private toasterMessageService: ToasterMessageService,
    public readonly sharedService: SharedService,
    private readonly _breakpointObserver: BreakpointObserver,
    private godModeService: GodModeService,
    private readonly planPrompt: PlanPromptService,
    private readonly mailboxConnectBridge: MailboxConnectBridgeService,
    private readonly router: Router,
  ) { }

  ngOnInit(): void {
    // SD-1248: an invitee who signed up on the way to a practice invitation lands on the first
    // signed-in page (sign-up ends on Home); take them back to the invitation.
    const pendingInvite = PracticeInviteReturn.pending();
    if (pendingInvite && this.router.url !== pendingInvite) {
      PracticeInviteReturn.forget();
      this.router.navigateByUrl(pendingInvite);
    }
    // SD-1229: "not on your plan" answered with the upgrade modal, on every page.
    this.planPrompt.start();
    // SD-1229: an Outlook connect cannot post back to this window; see the service.
    this.mailboxConnectBridge.start();
    this.setCustomJs();
    this.userService.loggedUserDetails.next(JSON.parse(this.userService.upUserDetails));
    this.subscriptions.push(this.userService.loggedUserDetails.subscribe(data => {
      this.userDetails = data || [];
    }));
    const storedUser = localStorage.getItem('GodModeUser');
    if (storedUser) {
      this.godModeUser = JSON.parse(storedUser);
      this.godModeService.enterGodMode(this.godModeUser);
    }

    this.subscriptions.push(
      this.godModeService.godModeUser$.subscribe(user => {
        this.godModeUser = user;
      })
    );
    this.getAllOrganizationAndUserDetails();

    this._breakpointObserver.observe([
      "(max-width: 768px) and (min-width: 429px)",
      "(max-width: 429px)",
    ]).subscribe((result: BreakpointState) => {
      if (result.matches) {
        if (result.breakpoints['(max-width: 429px)']) {
          this.sharedService.setScreenBrakepoint(ScreenBrakepoints.MaxWidth428px);
        } else {
          this.sharedService.setScreenBrakepoint(ScreenBrakepoints.MaxWidth768px);
        }
      } else {
        this.sharedService.setScreenBrakepoint(ScreenBrakepoints.MinWidth769px);
      }
    });
  }

  setCustomJs() {
    (document.getElementById('custom-js') as HTMLScriptElement).src = 'assets/js/custom.js';
  }

  //Get companyInfo for quickbook
  GetAllCompanyInfoAndUserDetails() {

    //Get all companyInfo List from CompanyInfo_QuickBook table
    this.organizationService.getOrganization('QuickBooks/CompanyInfoGet').pipe().subscribe((response: any) => {

      if (response.status == 0) {
        this.organizationService.organizationList.next(response.data);
      } else {

        this.organizationService.organizationList.next(null);
        this.toasterMessageService.displayToasterState(ToasterTypes.Error, 'Error', response.message);
        this.authService.signOut();

      }
    }, err => {

      this.organizationService.organizationList.next(null);
      this.userService.loggedUserDetails.next(null);
      this.toasterMessageService.displayToasterState(ToasterTypes.Error, 'Error', err.statusText);
      this.authService.signOut();

    });
  }

  getAllOrganizationAndUserDetails() {

    this.organizationService.getOrganization('Organisation/OrganisationGet').pipe().subscribe((response: any) => {
      if (response.status === 1) {
        this.organizationService.organizationList.next(response.data);
      } else {
        this.toasterMessageService.displayToasterState(ToasterTypes.Error, 'Error', response.message);
        this.authService.signOut();
      }
    }, err => {
      this.organizationService.organizationList.next(null);
      this.userService.loggedUserDetails.next(null);
      this.toasterMessageService.displayToasterState(ToasterTypes.Error, 'Error', err.statusText);
      this.authService.signOut();
    });
  }

  exitGodMode() {
    const userModel = {
      userId: this.godModeUser.id,
      organizationId: this.godModeUser.organization_Id
    }
    this.userService.exitGodMode(userModel).pipe().subscribe((res: any) => {
      localStorage.removeItem('GodModeUser');
      localStorage.setItem('token', res.access_token);
      localStorage.setItem('AccessToken', res.access_token);
      localStorage.setItem('UpUserDetails', JSON.stringify(res.user));
       localStorage.removeItem('isImpersonating');
      this.setUser(res);
      window.location.reload();
    });
  }

  setUser(response) {

    sessionStorage.UserName = response.user.name;
    sessionStorage.Id = response.user.id;
    sessionStorage.UserId = response.user.userId;
    localStorage.setItem('UserName', response.user.name);
    localStorage.setItem('UserId', response.user.userId);
    localStorage.setItem('Id', response.user.id);
    localStorage.setItem('IsLogedin', 'true');
    if (response.user.newOrgID != null) {
      localStorage.setItem('SelectedOrganization', response.user.newOrgID);
    }
    else {
      localStorage.setItem('SelectedOrganization', response.user.organizationId);
    }
    localStorage.setItem('Email', response.user.email);
    localStorage.setItem('CreatedAt', response.user.created_date);
    localStorage.setItem("roleId", response.user.role);
    if (response.user.newOrgID != null) {
      sessionStorage.organizationId = response.user.newOrgID;
      sessionStorage.selectedOrganization = response.user.newOrgID;
    }

    else if (response.user.organizationId) {
      sessionStorage.organizationId = response.user.organizationId;
      sessionStorage.selectedOrganization = response.user.organizationId;
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }
}